import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

/**
 * An explanation behind a small grey circled (i) after a title (Dominik, 21.10.2026: hints are not printed under the
 * content). A tap opens the text in a bubble below, a second tap on the icon or the bubble, Escape or a tap elsewhere
 * closes it. Put it inside a TitleInfo (or a CardHeader / TopBar with `info`), which positions the bubble.
 */
export function InfoTip({ text, label, testId }: { text: ReactNode; label: string; testId?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);
  return (
    <span className="info-wrap" ref={root}>
      <button
        type="button"
        className={open ? 'info is-open' : 'info'}
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        onClick={(e) => {
          // in a switch's row (a <label>) the tap opens the explanation, it never flips the switch
          e.preventDefault();
          setOpen(!open);
        }}
        data-testid={testId}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5" />
          <path className="info-dot" d="M12 7.6v.01" />
        </svg>
      </button>
      <span
        id={id}
        className="info-pop"
        role="note"
        hidden={!open}
        onClick={(e) => {
          e.preventDefault();
          setOpen(false);
        }}
        data-testid={testId ? `${testId}-text` : undefined}
      >
        {text}
      </span>
    </span>
  );
}

/** A title with its (i): the bubble opens below the title, aligned with its start. */
export function TitleInfo({
  children,
  info,
  label,
  testId,
}: {
  children: ReactNode;
  info?: ReactNode;
  label: string;
  testId?: string;
}) {
  if (!info) return <>{children}</>;
  return (
    <span className="title-info">
      {children}
      <InfoTip text={info} label={label} testId={testId} />
    </span>
  );
}

/**
 * The app's own reading of the figures (worked out from the shots, not by Claude): an icon in front, a green check when
 * everything is in range, otherwise a grey pulse.
 */
export function NoteLine({
  ok = false,
  icon,
  children,
  className,
  testId,
}: {
  ok?: boolean;
  icon?: IconName;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <p className={`note-line${ok ? ' is-ok' : ''}${className ? ` ${className}` : ''}`} data-testid={testId}>
      <Icon name={icon ?? (ok ? 'check' : 'pulse')} size="md" />
      <span>{children}</span>
    </p>
  );
}

/**
 * A tile for what is chosen (the coffee of the next shot): a small caps label, the name, a line below, and the action
 * at the right ("ÄNDERN ›"). The whole tile is the button.
 */
export function PickTile({
  eyebrow,
  title,
  sub,
  action,
  onClick,
  testId,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  action: ReactNode;
  onClick?: () => void;
  testId?: string;
}) {
  const body = (
    <>
      <span className="pick-text">
        <span className="label pick-eyebrow">{eyebrow}</span>
        <span className="pick-title">{title}</span>
        {sub && <span className="hint pick-sub">{sub}</span>}
      </span>
      {onClick && (
        <span className="link-caps pick-action">
          {action}
          <Icon name="chevronRight" size="sm" />
        </span>
      )}
    </>
  );
  return onClick ? (
    <button type="button" className="pick" onClick={onClick} data-testid={testId}>
      {body}
    </button>
  ) : (
    <div className="pick" data-testid={testId}>
      {body}
    </div>
  );
}

type FitPart = ReactNode | null | false | undefined;

/** The parts of a FitLine that show: without null, false, undefined and empty texts. */
export function fitParts(parts: readonly FitPart[]): ReactNode[] {
  return parts.filter((p) => p !== null && p !== false && p !== undefined && p !== '');
}

/**
 * A FitLine for a slot that is left out when there is nothing to say (a page's subtitle, a row's hint): undefined
 * without parts, so no empty line keeps its height (UI review 23.10.2026: a shot without a bean had an empty subtitle
 * that moved its title above the arrows).
 */
export function fitLineOf(parts: readonly FitPart[]): ReactElement | undefined {
  return fitParts(parts).length > 0 ? <FitLine parts={parts} /> : undefined;
}

/**
 * The parts of a summary in one line that never wraps (WRITING.md 0a, Dominik 23.10.2026: „Maximal 2 Zeilen ohne
 * Umbruch“): a part that does not fit drops out whole into a second line that is cut off, the last part first, with its
 * "·"; the first part always stays. Screen readers still read every part.
 */
export function FitLine({
  parts,
  className,
  testId,
}: {
  parts: readonly FitPart[];
  className?: string;
  testId?: string;
}) {
  const shown = fitParts(parts);
  if (shown.length === 0) return null;
  return (
    <span className={className ? `fit-line ${className}` : 'fit-line'} data-testid={testId}>
      {shown.map((p, i) => (
        <span key={i} className="fit-part">
          {i > 0 && '\u00a0· '}
          {p}
        </span>
      ))}
    </span>
  );
}
